/**
 * @file Bullet entity factory.
 *
 * Bullets are the highest-churn entity in the game, so everything they touch is
 * pooled: the entity object comes from the world pool and the mesh from the
 * prefab pool. Firing therefore allocates nothing after warm-up (GDD §19).
 */

import * as THREE from 'three';
import { Transform, Velocity, Collider, Renderable, Projectile } from '../components/index.js';

/**
 * Spawns a projectile travelling along a direction.
 *
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @param {object} options Projectile setup.
 * @param {THREE.Vector3} options.position Muzzle position.
 * @param {THREE.Vector3} options.direction Normalised travel direction.
 * @param {import('../components/Weapon.js').Weapon} options.weapon Weapon that fired it.
 * @param {import('../ecs/Entity.js').Entity} options.owner Shooter.
 * @param {number} [options.damage] Damage override, after buffs are applied.
 * @param {import('../combat/Shot.js').ShotRecord} [options.shot] Ledger shared by
 *   every projectile of the same trigger pull.
 * @returns {import('../ecs/Entity.js').Entity}
 */
export function createBullet(ctx, { position, direction, weapon, owner, damage, shot = null }) {
  const { world, meshFactory } = ctx;

  const entity = world.createEntity('bullet');

  const transform = entity.add(new Transform(position.x, position.y, position.z));
  transform.rotationY = Math.atan2(direction.x, direction.z);

  const velocity = entity.add(new Velocity(weapon.projectileSpeed, 0, 0));
  velocity.linear.copy(direction).multiplyScalar(weapon.projectileSpeed);
  // Bullets fly straight: the movement system must not steer or damp them.
  velocity.steered = false;

  entity.add(
    new Projectile({
      damage: damage ?? weapon.damage,
      range: weapon.range,
      ownerId: owner.id,
      pierce: weapon.pierce,
      shot,
      falloffStart: weapon.falloffStart,
      falloffMin: weapon.falloffMin,
      knockback: weapon.knockback
    })
  );

  entity.add(
    Collider.fromRadius(weapon.projectileRadius, weapon.projectileLayer, weapon.projectileMask)
  );

  entity.add(
    new Renderable(weapon.projectilePrefab, meshFactory.acquire(weapon.projectilePrefab), {
      yOffset: 0,
      faceVelocity: true
    })
  );

  return entity;
}
